import axios from 'axios';
import { useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useSubmissionLock } from '../hooks/useSubmissionLock';

const API_BASE = process.env.REACT_APP_API_URL;

function Signup() {
  const navigate = useNavigate();
  const { setUser, refreshMe } = useContext(AuthContext);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    marketing: false,
  });

  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { run: runSubmission, isPending } = useSubmissionLock();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting || isPending('signup')) return;

    await runSubmission('signup', async () => {
      setError('');
      setSubmitting(true);

      if (!API_BASE) {
        setError('Missing REACT_APP_API_URL (frontend environment variable).');
        setSubmitting(false);
        return;
      }

      try {
        const payload = {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim().toLowerCase(),
          password: form.password,
          marketing: form.marketing,

          // Legacy compatibility for any old backend code still reading these
          name: form.firstName.trim(),
          lname: form.lastName.trim(),
        };

        const res = await axios.post(`${API_BASE}/api/auth/register`, payload, {
          withCredentials: true,
        });

        const token = res.data?.token;
        const user = res.data?.user;

        if (token) {
          localStorage.setItem('accessToken', token);
        }

        if (user) {
          setUser(user);
        }

        if (token && typeof refreshMe === 'function') {
          await refreshMe();
        }

        navigate('/dashboard');
      } catch (err) {
        console.error('Signup failed:', err.response?.data || err.message);
        setError(
          err.response?.data?.error ||
            err.response?.data?.message ||
            'Signup failed. Please try again.'
        );
      } finally {
        setSubmitting(false);
      }
    });
  };

  return (
    <div className="flex h-screen">
      {/* Left Side - Glass Form */}
      <div className="w-full md:w-1/2 flex items-center justify-center bg-gradient-to-br from-[#111] via-[#1a1a1a] to-[#2b2b2b] p-8">
        <div className="bg-white/10 backdrop-blur-md border border-white/10 rounded-2xl p-10 w-full max-w-md shadow-2xl">
          <center>
            <img
              src="https://cdn.prod.website-files.com/638fda8f8e2f041af84cb5a7/6824d581ae24f69d45e26536_beem%20logo%203.png"
              alt="Beem Logo"
              className="w-28 mb-6"
            />
          </center>

          <h2 className="text-3xl font-bold mb-2 text-center">Create your account</h2>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="firstName" className="block mb-1 text-sm font-medium text-gray-300">
                First Name
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={form.firstName}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 rounded-lg bg-white/20 text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A]"
              />
            </div>

            <div>
              <label htmlFor="lastName" className="block mb-1 text-sm font-medium text-gray-300">
                Last Name
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={form.lastName}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 rounded-lg bg-white/20 text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A]"
              />
            </div>

            <div>
              <label htmlFor="email" className="block mb-1 text-sm font-medium text-gray-300">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 rounded-lg bg-white/20 text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A]"
              />
            </div>

            <div>
              <label htmlFor="password" className="block mb-1 text-sm font-medium text-gray-300">
                Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={form.password}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 rounded-lg bg-white/20 text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-[#AF9B8A]"
              />
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="marketing"
                name="marketing"
                checked={form.marketing}
                onChange={handleChange}
                className="accent-[#AF9B8A]"
              />
              <label htmlFor="marketing" className="text-sm text-gray-400">
                I want to receive updates and offers.
              </label>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#AF9B8A] hover:bg-[#9c8a79] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3 px-4 rounded-lg transition-colors"
              aria-busy={submitting}
            >
              {submitting ? 'Creating account...' : 'Sign Up'}
            </button>

            {error && <p className="text-red-400 text-center">{error}</p>}
          </form>

          <p className="mt-6 text-sm text-center text-gray-300">
            Already have an account?{' '}
            <Link to="/sign-in" className="text-[#AF9B8A] hover:underline">
              Log in
            </Link>
          </p>
        </div>
      </div>

      {/* Right Side - City Illustration */}
      <div className="hidden md:block w-1/2 relative">
        <img
          src="https://cdn.prod.website-files.com/638fda8f8e2f041af84cb5a7/68e10ee19b260e7f43fddd20_ChatGPT%20Image%20Oct%204%2C%202025%2C%2012_26_41%20PM.png"
          alt="City illustration"
          className="object-cover w-full h-full"
        />
        <div className="absolute inset-0 bg-gradient-to-l from-black/20 to-transparent" />
      </div>
    </div>
  );
}

export default Signup;
